import type {
    FishGroupOperationRequest,
    SiteDelousingRequest,
    SiteRelocateFishGroupRequest,
} from '@/services/types';

/** A bioscope that moves into a pen together with the fish. */
export interface SiteBioscopeMove {
    bioscopeId: number;
    targetCageId: number;
}

/** How the fish were deloused; both parts are optional. */
export interface SiteDelousingDetails {
    treatmentMethod?: string;
    notes?: string;
}

/** A whole group into an empty pen of the same site. */
export interface SiteMoveInput {
    fishGroupId: number;
    targetCageId: number;
    /** ISO timestamp; see `toOperationTimestamp`. */
    date: string;
    notes?: string;
    bioscopeMoves: SiteBioscopeMove[];
    /** Registered with the move, on its date, in the same request. */
    delousing?: SiteDelousingDetails;
}

export interface SiteDelousingInput extends SiteDelousingDetails {
    fishGroupId: number;
    /** ISO timestamp; see `toOperationTimestamp`. */
    date: string;
}

export interface SiteSplitDestination {
    name: string;
    cageId: number;
    fishCount: number | null;
}

/** One group into several pens; a grading is a split labelled `grading`. */
export interface SiteSplitInput {
    sourceId: number;
    /** ISO timestamp; see `toOperationTimestamp`. */
    date: string;
    destinations: SiteSplitDestination[];
    grading: boolean;
    bioscopeMoves: SiteBioscopeMove[];
}

export interface SiteMergeSource {
    fishGroupId: number;
    fishCount: number | null;
}

/** Several groups into one pen. */
export interface SiteMergeInput {
    sources: SiteMergeSource[];
    /** ISO timestamp; see `toOperationTimestamp`. */
    date: string;
    name: string;
    cageId: number;
    bioscopeMoves: SiteBioscopeMove[];
}

/** The `reason` that labels a split as a grading on the board. */
export const GRADING_REASON = 'grading';

function trimmed(value: string | undefined): string | undefined {
    const text = value?.trim();
    return text ? text : undefined;
}

function delousingDetails(details: SiteDelousingDetails): SiteDelousingDetails {
    const treatmentMethod = trimmed(details.treatmentMethod);
    const notes = trimmed(details.notes);
    return { ...(treatmentMethod && { treatmentMethod }), ...(notes && { notes }) };
}

function positiveCount(count: number | null): { estimatedFishCount?: number } {
    return count !== null && count > 0 ? { estimatedFishCount: count } : {};
}

export function mapSiteMoveToRequest(input: SiteMoveInput): SiteRelocateFishGroupRequest {
    const notes = trimmed(input.notes);
    return {
        targetCageId: input.targetCageId,
        relocationDate: input.date,
        ...(notes && { notes }),
        ...(input.bioscopeMoves.length > 0 && { bioscopeMovements: input.bioscopeMoves }),
        ...(input.delousing && { delousing: delousingDetails(input.delousing) }),
    };
}

export function mapSiteDelousingToRequest(input: SiteDelousingInput): SiteDelousingRequest {
    return { eventDate: input.date, ...delousingDetails(input) };
}

export function mapSiteSplitToRequest(input: SiteSplitInput): FishGroupOperationRequest {
    return {
        sources: [{ fishGroupId: input.sourceId }],
        destinations: input.destinations.map((destination) => ({
            name: destination.name.trim(),
            cageId: destination.cageId,
            ...positiveCount(destination.fishCount),
            receivesFrom: [
                { sourceFishGroupId: input.sourceId, ...positiveCount(destination.fishCount) },
            ],
        })),
        operationDate: input.date,
        ...(input.grading && { reason: GRADING_REASON }),
        ...(input.bioscopeMoves.length > 0 && { bioscopeMovements: input.bioscopeMoves }),
    };
}

export function mapSiteMergeToRequest(input: SiteMergeInput): FishGroupOperationRequest {
    const known = input.sources.map((source) => source.fishCount);
    const total = known.every((count) => count !== null)
        ? known.reduce<number>((sum, count) => sum + (count ?? 0), 0)
        : null;
    return {
        sources: input.sources.map((source) => ({ fishGroupId: source.fishGroupId })),
        destinations: [
            {
                name: input.name.trim(),
                cageId: input.cageId,
                ...positiveCount(total),
                receivesFrom: input.sources.map((source) => ({
                    sourceFishGroupId: source.fishGroupId,
                    ...positiveCount(source.fishCount),
                })),
            },
        ],
        operationDate: input.date,
        ...(input.bioscopeMoves.length > 0 && { bioscopeMovements: input.bioscopeMoves }),
    };
}

function groupIds(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((group: unknown) =>
        typeof group === 'object' && group !== null && 'id' in group && typeof group.id === 'number'
            ? [group.id]
            : []
    );
}

/**
 * Every group an operation touched, from its response (closed sources, created groups) and
 * the ids the request already knew. The generated response type is untyped, so it is read
 * defensively.
 */
export function operationGroupIds(response: unknown, known: readonly number[]): number[] {
    const body = typeof response === 'object' && response !== null ? response : {};
    const closed = 'sourcesClosed' in body ? groupIds(body.sourcesClosed) : [];
    const created = 'destinationsCreated' in body ? groupIds(body.destinationsCreated) : [];
    return [...new Set([...known, ...closed, ...created])];
}
